/**
 * Approvals Inbox — Just-In-Time Platform Elevation admin surface.
 *
 * Consumes:
 *   GET  /api/platform-access/approvals                — filterable listing
 *   GET  /api/platform-access/approvals/pending-count  — sidebar badge value
 *   GET  /api/platform-access/{id}/audit               — per-elevation audit trail
 *   POST /api/platform-access/{id}/approve             — approve (MFA required)
 *   POST /api/platform-access/{id}/deny                — deny + reason (MFA required)
 *   POST /api/platform-access/{id}/extend              — extend (MFA required)
 *   POST /api/platform-access/{id}/admin-revoke        — approver revoke (MFA required)
 *
 * RBAC: Platform Super Admins + Compliance Officers only. The backend
 * enforces this — this page renders a friendly refusal for others.
 *
 * Design contract:
 *   - No inline PHI. Reason + audit metadata only.
 *   - MFA confirmation dialog before every mutation. Never store the OTP.
 *   - Live pending-count via polling every 10s so the sidebar stays honest.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '../services/api';
import Layout from '../components/Layout';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet';
import {
  ShieldCheck,
  CheckCircle,
  XCircle,
  Timer,
  StopCircle,
  ArrowsClockwise,
  Eye,
  Warning,
} from '@phosphor-icons/react';

const STATUS_BADGE = {
  pending_approval: 'bg-amber-100 text-amber-900',
  active: 'bg-emerald-100 text-emerald-800',
  denied: 'bg-red-100 text-red-800',
  revoked: 'bg-slate-200 text-slate-800',
  expired: 'bg-slate-100 text-slate-600',
};

const humanizeError = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.length < 200) return detail;
  if (detail?.message) return detail.message;
  if (err?.response?.status === 403) return 'Not authorised for this action.';
  if (err?.response?.status === 404) return 'Elevation no longer available.';
  return 'Action failed. Please retry.';
};

const secondsToClock = (isoExpires) => {
  const ms = new Date(isoExpires).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m ${sec.toString().padStart(2, '0')}s`;
};

const ElevationApprovalsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [forbidden, setForbidden] = useState(false);
  const [tick, setTick] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState('pending_approval');
  const [agencyFilter, setAgencyFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [afterFilter, setAfterFilter] = useState('');
  const [beforeFilter, setBeforeFilter] = useState('');

  // Modal + drawer state
  const [selected, setSelected] = useState(null); // row for detail drawer
  const [auditTrail, setAuditTrail] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null); // {action, row, extra}

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (statusFilter && statusFilter !== 'all') p.set('status', statusFilter);
    if (agencyFilter) p.set('agency_id', agencyFilter);
    if (userFilter) {
      // Treat @ as email, else user_id.
      p.set(userFilter.includes('@') ? 'user_email' : 'user_id', userFilter);
    }
    if (roleFilter) p.set('role', roleFilter);
    if (afterFilter) p.set('requested_after', afterFilter);
    if (beforeFilter) p.set('requested_before', beforeFilter);
    p.set('limit', '100');
    return p.toString();
  }, [statusFilter, agencyFilter, userFilter, roleFilter, afterFilter, beforeFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/platform-access/approvals?${query}`);
      setRows(data.items || []);
      setTotal(data.total || 0);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(humanizeError(e));
      }
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    load();
  }, [load, tick]);

  // Tick every 5 seconds so the countdown column stays fresh.
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 5000);
    return () => clearInterval(t);
  }, []);

  const openAudit = async (row) => {
    setSelected(row);
    try {
      const { data } = await api.get(`/platform-access/${row.id}/audit`);
      setAuditTrail(data.events || []);
    } catch (e) {
      toast.error(humanizeError(e));
      setAuditTrail([]);
    }
  };

  const startAction = (action, row) => {
    setConfirmModal({ action, row });
  };

  const runAction = async ({ action, row, otp, extra }) => {
    try {
      let path, body;
      if (action === 'approve') {
        path = `/platform-access/${row.id}/approve`;
        body = { otp };
      } else if (action === 'deny') {
        path = `/platform-access/${row.id}/deny`;
        body = { otp, reason: extra.reason };
      } else if (action === 'extend') {
        path = `/platform-access/${row.id}/extend`;
        body = { otp, additional_minutes: Number(extra.additional_minutes) };
      } else if (action === 'revoke') {
        path = `/platform-access/${row.id}/admin-revoke`;
        body = { otp, reason: extra.reason || '' };
      } else {
        throw new Error(`unknown action: ${action}`);
      }
      await api.post(path, body);
      toast.success(`Elevation ${action} recorded.`);
      setConfirmModal(null);
      setSelected(null);
      setTick((n) => n + 1);
    } catch (e) {
      toast.error(humanizeError(e));
    }
  };

  if (forbidden) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-16 text-center" data-testid="elevation-approvals-forbidden">
          <Warning size={48} className="mx-auto text-amber-500 mb-4" weight="duotone" />
          <h1 className="text-xl font-semibold text-slate-900">Approvals Inbox restricted</h1>
          <p className="text-sm text-slate-500 mt-2">
            Only Platform Super Admins and Compliance Officers can review
            elevation requests. Ask a security lead for the{' '}
            <code>compliance_officer</code> role if you need access.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="elevation-approvals-page">
        <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Elevation Approvals
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Just-in-time platform access requests. Every action is
              MFA-gated and recorded in the tamper-evident audit chain.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-amber-100 text-amber-900" data-testid="approvals-total-badge">
              {total} total
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTick((n) => n + 1)}
              disabled={loading}
              data-testid="approvals-refresh-btn"
            >
              <ArrowsClockwise size={16} className="mr-1.5" />
              Refresh
            </Button>
          </div>
        </header>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="approvals-filter-status">
                <SelectValue placeholder="Any" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any status</SelectItem>
                <SelectItem value="pending_approval">Pending approval</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="denied">Denied</SelectItem>
                <SelectItem value="revoked">Revoked</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Agency ID</label>
            <Input
              value={agencyFilter}
              onChange={(e) => setAgencyFilter(e.target.value.trim())}
              placeholder="agency-…"
              data-testid="approvals-filter-agency"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">User (id or email)</label>
            <Input
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value.trim())}
              placeholder="user@example.com"
              data-testid="approvals-filter-user"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Requester role</label>
            <Input
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value.trim())}
              placeholder="e.g. support_agent"
              data-testid="approvals-filter-role"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Requested after</label>
            <Input
              type="datetime-local"
              value={afterFilter}
              onChange={(e) => setAfterFilter(e.target.value)}
              data-testid="approvals-filter-after"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Requested before</label>
            <Input
              type="datetime-local"
              value={beforeFilter}
              onChange={(e) => setBeforeFilter(e.target.value)}
              data-testid="approvals-filter-before"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Requester</th>
                <th className="text-left px-4 py-2">Agency</th>
                <th className="text-left px-4 py-2">Scope</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Requested</th>
                <th className="text-left px-4 py-2">Expires</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-400">Loading…</td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-testid="approvals-empty">
                    No matching elevations.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50" data-testid={`approvals-row-${r.id}`}>
                  <td className="px-4 py-2 text-slate-700">
                    <div className="text-sm">{r.user_email}</div>
                    <div className="text-xs text-slate-400">{r.requester_role || '—'}</div>
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    <div className="text-sm">{r.target_agency_name}</div>
                    <div className="text-xs text-slate-400 font-mono">{r.target_agency_id}</div>
                  </td>
                  <td className="px-4 py-2 text-xs">{r.scope}</td>
                  <td className="px-4 py-2">
                    <Badge className={STATUS_BADGE[r.status] || 'bg-slate-100 text-slate-700'}>
                      {r.status.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">{formatDateTime(r.requested_at)}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    {r.status === 'active' ? (
                      <span className="inline-flex items-center gap-1" title={r.expires_at}>
                        <Timer size={12} /> {secondsToClock(r.expires_at)}
                      </span>
                    ) : (
                      formatDateTime(r.expires_at) || '—'
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <Button size="sm" variant="outline" onClick={() => openAudit(r)} data-testid={`approvals-view-${r.id}`}>
                        <Eye size={14} className="mr-1" /> Details
                      </Button>
                      {r.status === 'pending_approval' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-700"
                            onClick={() => startAction('approve', r)}
                            data-testid={`approvals-approve-${r.id}`}
                          >
                            <CheckCircle size={14} className="mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-700"
                            onClick={() => startAction('deny', r)}
                            data-testid={`approvals-deny-${r.id}`}
                          >
                            <XCircle size={14} className="mr-1" /> Deny
                          </Button>
                        </>
                      )}
                      {r.status === 'active' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => startAction('extend', r)}
                            data-testid={`approvals-extend-${r.id}`}
                          >
                            <Timer size={14} className="mr-1" /> Extend
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-700"
                            onClick={() => startAction('revoke', r)}
                            data-testid={`approvals-revoke-${r.id}`}
                          >
                            <StopCircle size={14} className="mr-1" /> Revoke
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <DetailDrawer
        row={selected}
        auditTrail={auditTrail}
        onClose={() => {
          setSelected(null);
          setAuditTrail([]);
        }}
      />

      <ConfirmDialog
        state={confirmModal}
        onCancel={() => setConfirmModal(null)}
        onSubmit={runAction}
      />
    </Layout>
  );
};

const DetailDrawer = ({ row, auditTrail, onClose }) => {
  if (!row) return null;
  return (
    <Sheet open={!!row} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="approvals-detail-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShieldCheck size={20} className="text-emerald-600" />
            {row.target_agency_name}
          </SheetTitle>
          <SheetDescription>{row.reason}</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Meta label="Requester" value={row.user_email} />
            <Meta label="Requester role" value={row.requester_role || '—'} />
            <Meta label="Scope" value={row.scope} />
            <Meta label="Status" value={row.status.replace('_', ' ')} />
            <Meta label="Requested at" value={formatDateTime(row.requested_at)} />
            <Meta label="Starts at" value={formatDateTime(row.starts_at)} />
            <Meta label="Expires at" value={formatDateTime(row.expires_at)} />
            <Meta label="Duration" value={`${row.duration_minutes} min`} />
            {row.approved_by_email && (
              <>
                <Meta label="Approved by" value={row.approved_by_email} />
                <Meta label="Approved at" value={formatDateTime(row.approved_at)} />
              </>
            )}
            {row.deny_reason && <Meta label="Deny reason" value={row.deny_reason} full />}
            {row.revoke_reason && <Meta label="Revoke reason" value={row.revoke_reason} full />}
          </div>

          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">Audit trail (immutable)</p>
            {auditTrail.length === 0 ? (
              <div className="text-xs text-slate-400 py-2">No audit rows yet.</div>
            ) : (
              <ol className="space-y-1.5 border border-slate-200 rounded-md p-2 bg-slate-50" data-testid="approvals-audit-trail">
                {auditTrail.map((e, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs">
                    <span className="font-mono text-slate-400 shrink-0">#{e.seq}</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-700">{e.action}</span>
                        <span className="text-slate-400">{formatDateTime(e.ts)}</span>
                      </div>
                      <div className="text-slate-500">
                        {e.actor?.email || e.actor?.id}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const ConfirmDialog = ({ state, onCancel, onSubmit }) => {
  const [otp, setOtp] = useState('');
  const [reason, setReason] = useState('');
  const [addMinutes, setAddMinutes] = useState(30);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (state) {
      setOtp('');
      setReason('');
      setAddMinutes(30);
    }
  }, [state]);

  if (!state) return null;
  const { action, row } = state;
  const needsReason = action === 'deny' || action === 'revoke';
  const reasonRequired = action === 'deny';
  const needsMinutes = action === 'extend';

  const canSubmit = (() => {
    if (!otp || otp.length < 6) return false;
    if (reasonRequired && reason.trim().length < 5) return false;
    if (needsMinutes && (!addMinutes || addMinutes < 5)) return false;
    return true;
  })();

  const submit = async () => {
    setSubmitting(true);
    await onSubmit({
      action,
      row,
      otp,
      extra: { reason: reason.trim(), additional_minutes: addMinutes },
    });
    setSubmitting(false);
  };

  const TITLES = {
    approve: 'Approve elevation',
    deny: 'Deny elevation',
    extend: 'Extend elevation',
    revoke: 'Revoke elevation',
  };

  return (
    <Dialog open={!!state} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent data-testid="approvals-confirm-dialog">
        <DialogHeader>
          <DialogTitle>{TITLES[action]}</DialogTitle>
          <DialogDescription>
            {row.user_email} → {row.target_agency_name}. This action is
            immutable and recorded in the audit chain.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {needsReason && (
            <div>
              <label className="text-xs font-medium text-slate-600">
                Reason {reasonRequired ? '(required, ≥ 5 chars)' : '(optional)'}
              </label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                placeholder={reasonRequired ? 'Why is this being denied?' : 'Why is this being revoked?'}
                data-testid="approvals-confirm-reason"
              />
            </div>
          )}
          {needsMinutes && (
            <div>
              <label className="text-xs font-medium text-slate-600">Additional minutes</label>
              <Input
                type="number"
                min={5}
                max={480}
                value={addMinutes}
                onChange={(e) => setAddMinutes(Number(e.target.value))}
                data-testid="approvals-confirm-minutes"
              />
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-600">
              MFA code (TOTP or backup code)
            </label>
            <Input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value.trim())}
              placeholder="123456"
              autoFocus
              data-testid="approvals-confirm-mfa"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit || submitting} data-testid="approvals-confirm-submit">
            {submitting ? 'Working…' : `Confirm ${action}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Meta = ({ label, value, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <p className="text-xs font-medium text-slate-500">{label}</p>
    <p className="text-sm text-slate-800 break-words">{value ?? '—'}</p>
  </div>
);

export default ElevationApprovalsPage;
